import React from 'react';
import styled from 'styled-components';

import { Command, State, tabActions } from '@store/index';
import { useDispatch, useSelector } from 'react-redux';
import { NodeInputExpression } from '@common/Node/NodeSidebar';
import { NodeField } from '@components/Flow/Nodes';
import { Divider } from '@mui/material';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.1rem 1.25rem 1rem;
  padding-bottom: 1.5rem;
`;

interface Props {
  command: Command;
  field: NodeField;
}

export const After: React.FC<Props> = ({ command, field }) => {
  const dispatch = useDispatch();

  const state = command.state;
  const commands = useSelector((state: State) => state.tabs.active.commands);

  if (!state?.after) return null;

  const tool_choice = state?.after?.tool_choice;
  const show_feedback = state?.after?.show_feedback;
  const show_help_action = state?.after?.show_help_action;
  const max_tool_calls = state?.after?.max_tool_calls;
  const max_tool_reached_system = state?.after?.max_tool_reached_system;
  const max_conversation_history = state?.after?.max_conversation_history;
  const jsonEnabled = state?.json_mode?.enabled;

  const TTShowHelpAction =
    'A variable named show_help_action will be returned in the metadata of the response to this request. You can set this value in the AI Completion node or an AI Tool node. Your frontend can use this variable to show or hide a button to contact your support team.';
  const TTToolChoice = 'You can set this value to force the LLM to execute this tool next before responding.';
  const TTShowFeedback = [
    'A variable named show_feedback will be returned in the metadata of the response to this request.',
    'You can set this value in the AI Completion node or an AI Tool node. Your frontend can use this variable to show or hide a feedback button for the answer.',
  ].join('\n');

  const hasTools = command.connections.some((connection) => connection.source_anchor.variant === 'plugin');

  const isCompletion = command.config.command_id === 'llm.completion';

  const tool_names: any[] = commands
    .filter((command) => command.config.identifier === 'Tool')
    .map((tool) => tool?.state?.name || null)
    .filter((name) => name && name !== command?.state?.name)
    .map((name) => ({ label: name, value: name }));

  return (
    <Container>
      {isCompletion && (
        <NodeInputExpression
          field={field}
          command={command}
          override={{
            title: 'Max Conversation History',
            placeholder: '10',
            tooltip:
              'This setting defines how many previous message pairs can be retrieved. A message pair is a question from the user, and an answer from the bot. If you set this value to 10 for example, there will be 20 messages max added to the conversation array before sending the request.\nIf you are using a model with a small token size, you should reduce this value.',
            value: max_conversation_history,
            update: (fieldName, value) => {
              dispatch(
                tabActions.nodeInputNestedUpdate({
                  nodeId: command.node.id,
                  fieldId: field.id,
                  nestedFieldName: 'max_conversation_history',
                  fieldName,
                  value,
                })
              );
            },
          }}
        />
      )}
      {!jsonEnabled && (
        <NodeInputExpression
          field={field}
          command={command}
          options={[{ label: 'none', value: 'none' }, { label: 'auto', value: 'auto' }, ...tool_names]}
          override={{
            title: 'Next Tool Choice',
            tooltip: TTToolChoice,
            value: tool_choice,
            optionType: true,
            update: (fieldName, value) => {
              dispatch(
                tabActions.nodeInputNestedUpdate({
                  nodeId: command.node.id,
                  fieldId: field.id,
                  nestedFieldName: 'tool_choice',
                  fieldName,
                  value,
                })
              );
            },
          }}
        />
      )}
      <NodeInputExpression
        field={field}
        command={command}
        override={{
          title: 'Show Feedback',
          tooltip: TTShowFeedback,
          value: show_feedback,
          optionType: true,
          update: (fieldName, value) => {
            dispatch(
              tabActions.nodeInputNestedUpdate({
                nodeId: command.node.id,
                fieldId: field.id,
                nestedFieldName: 'show_feedback',
                fieldName,
                value,
              })
            );
          },
        }}
      />
      <NodeInputExpression
        field={field}
        command={command}
        override={{
          title: 'Show Help Action',
          tooltip: TTShowHelpAction,
          value: show_help_action,
          optionType: true,
          update: (fieldName, value) => {
            dispatch(
              tabActions.nodeInputNestedUpdate({
                nodeId: command.node.id,
                fieldId: field.id,
                nestedFieldName: 'show_help_action',
                fieldName,
                value,
              })
            );
          },
        }}
      />
      {(max_tool_calls || max_tool_reached_system) && hasTools ? <Divider /> : null}
      {max_tool_calls && hasTools && !jsonEnabled && (
        <NodeInputExpression
          field={field}
          command={command}
          override={{
            title: 'Max Tool Calls',
            tooltip: 'Specify the max number of tools that can be called before the LLM has to respond. Defaults to 3.',
            value: max_tool_calls,
            update: (fieldName, value) => {
              dispatch(
                tabActions.nodeInputNestedUpdate({
                  nodeId: command.node.id,
                  fieldId: field.id,
                  nestedFieldName: 'max_tool_calls',
                  fieldName,
                  value,
                })
              );
            },
          }}
        />
      )}
      {max_tool_reached_system && hasTools && !jsonEnabled && (
        <NodeInputExpression
          field={field}
          command={command}
          override={{
            title: 'Max Tools System Message',
            placeholder: 'This query is too complex, try a simpler one.',
            tooltip:
              'Optionally defined to change the system message after max tools has been reached. Useful for directing the user how to resolve the issue if it is known in advance why the max tools might be reached.',
            value: max_tool_reached_system,
            update: (fieldName, value) => {
              dispatch(
                tabActions.nodeInputNestedUpdate({
                  nodeId: command.node.id,
                  fieldId: field.id,
                  nestedFieldName: 'max_tool_reached_system',
                  fieldName,
                  value,
                })
              );
            },
          }}
        />
      )}
    </Container>
  );
};
